import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';
import { Menu, X } from 'lucide-react';
import siteData from '@/data/siteData.json';

export const Header = ({ onNavigate }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: 'Início', href: '#hero' },
    { label: 'Produtos', href: '#produtos' },
    { label: 'Marcas', href: '#marcas' },
    { label: 'Sobre', href: '#sobre' },
    { label: 'Contato', href: '#contato' },
    { label: 'Código de Ética', href: '/codigo-etica.html', external: true },
  ];

  const handleNavClick = (href, external = false) => {
    setIsOpen(false);
    if (external) {
      window.open(href, '_blank');
      return;
    }
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    if (onNavigate) onNavigate(href);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background/95 backdrop-blur-md shadow-md border-b border-border'
          : 'bg-transparent'
      }`}
    >
      <div className="section-container">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a
            href="#hero"
            onClick={(e) => {
              e.preventDefault();
              handleNavClick('#hero');
            }}
            className="flex items-center gap-3"
          >
            <img
              src={siteData.company.logo}
              alt={siteData.company.name}
              className={`h-10 md:h-12 w-auto transition-all duration-300 ${
                isScrolled ? '' : 'brightness-0 invert'
              }`}
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <button
                key={item.href}
                onClick={() => handleNavClick(item.href, item.external)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                  isScrolled
                    ? 'text-foreground hover:bg-accent'
                    : 'text-background/90 hover:text-background hover:bg-background/10'
                }`}
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden md:flex items-center gap-4">
            <Button
              onClick={() => handleNavClick('#contato')}
              className={`transition-all duration-300 ${
                isScrolled
                  ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                  : 'bg-background text-foreground hover:bg-background/90'
              }`}
            >
              Solicitar Orçamento
            </Button>
          </div>

          {/* Mobile Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button
                variant="ghost"
                size="icon"
                className={`${
                  isScrolled ? 'text-foreground' : 'text-background'
                }`}
              >
                <Menu className="h-6 w-6" />
                <span className="sr-only">Menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 bg-background">
              <SheetTitle className="sr-only">Menu de Navegação</SheetTitle>
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between py-4 border-b border-border">
                  <img
                    src={siteData.company.logo}
                    alt={siteData.company.name}
                    className="h-10 w-auto"
                  />
                </div>
                <nav className="flex flex-col gap-1 py-6">
                  {navItems.map((item) => (
                    <button
                      key={item.href}
                      onClick={() => handleNavClick(item.href, item.external)}
                      className="px-4 py-3 text-left text-foreground font-medium rounded-md hover:bg-accent transition-colors"
                    >
                      {item.label}
                    </button>
                  ))}
                </nav>
                <div className="mt-auto pb-6">
                  <Button
                    onClick={() => handleNavClick('#contato')}
                    className="w-full bg-primary text-primary-foreground"
                  >
                    Solicitar Orçamento
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
